import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Palette, Globe, Eye, EyeSlash } from "@phosphor-icons/react";
import axios from "axios";
import config from "../config";

const API = `${config.BACKEND_URL}/api`;

export default function WhiteLabelPage() {
  const { getAuthHeaders } = useAuth();
  const [wlConfig, setWlConfig] = useState({
    brand_name: "AI Assistant",
    brand_colors: { primary: "#002FA7" },
    custom_domain: "",
    remove_powered_by: false,
  });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // ✅ FIX: useCallback add kiya — ESLint exhaustive-deps error fix
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/white-label`, { headers: getAuthHeaders() });
      const data = res.data;
      const cfg = {
        brand_name: data.brand_name || "AI Assistant",
        brand_colors: data.brand_colors || { primary: "#002FA7" },
        custom_domain: data.custom_domain || "",
        remove_powered_by: data.remove_powered_by || false,
      };
      setWlConfig(cfg);
      setOriginalConfig(JSON.stringify(cfg));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (originalConfig) {
      setHasChanges(JSON.stringify(wlConfig) !== originalConfig);
    }
  }, [wlConfig, originalConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/white-label`, wlConfig, { headers: getAuthHeaders() });
      setOriginalConfig(JSON.stringify(wlConfig));
      setHasChanges(false);
      alert("White-label settings saved successfully!");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalConfig) {
      setWlConfig(JSON.parse(originalConfig));
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm font-mono uppercase tracking-[0.2em] text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  const primaryColor = wlConfig.brand_colors?.primary || "#002FA7";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">White-Label</h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Customize how your chatbot appears to your visitors. Pro plan only.
        </p>
      </div>

      <div className="border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Palette size={18} className="text-[#002FA7]" />
          <h2 className="font-heading text-lg font-bold">Brand Identity</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/40 mb-2 block">
              Brand Name
            </label>
            <input
              value={wlConfig.brand_name}
              onChange={(e) => setWlConfig({ ...wlConfig, brand_name: e.target.value })}
              placeholder="AI Assistant"
              className="w-full border border-[#D1D5DB] px-4 py-2 text-sm outline-none focus:border-[#002FA7] transition-colors"
            />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/40 mb-2 block">
              Primary Color
            </label>
            <div className="flex gap-3">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) =>
                  setWlConfig({
                    ...wlConfig,
                    brand_colors: { ...wlConfig.brand_colors, primary: e.target.value },
                  })
                }
                className="w-14 h-10 border border-[#D1D5DB] cursor-pointer"
              />
              <input
                value={primaryColor}
                onChange={(e) =>
                  setWlConfig({
                    ...wlConfig,
                    brand_colors: { ...wlConfig.brand_colors, primary: e.target.value },
                  })
                }
                className="flex-1 border border-[#D1D5DB] px-4 py-2 text-sm font-mono outline-none focus:border-[#002FA7]"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={18} className="text-[#002FA7]" />
          <h2 className="font-heading text-lg font-bold">Custom Domain</h2>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A]/40 mb-2 block">
            Your Domain
          </label>
          <input
            value={wlConfig.custom_domain}
            onChange={(e) => setWlConfig({ ...wlConfig, custom_domain: e.target.value })}
            placeholder="chat.yourdomain.com"
            className="w-full border border-[#D1D5DB] px-4 py-2 text-sm outline-none focus:border-[#002FA7] transition-colors"
          />
          <p className="text-xs text-[#6B7280] mt-2">
            Add a CNAME record pointing to <code className="bg-[#F3F4F6] px-1">botyfi.com</code>
          </p>
        </div>
      </div>

      <div className="border border-[#E5E7EB] p-6">
        <div className="flex items-center gap-2 mb-4">
          {wlConfig.remove_powered_by ? (
            <EyeSlash size={18} className="text-[#002FA7]" />
          ) : (
            <Eye size={18} className="text-[#002FA7]" />
          )}
          <h2 className="font-heading text-lg font-bold">Branding Options</h2>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="remove-powered-by"
            checked={wlConfig.remove_powered_by}
            onChange={(e) =>
              setWlConfig({ ...wlConfig, remove_powered_by: e.target.checked })
            }
            className="w-4 h-4 accent-[#002FA7]"
          />
          <label htmlFor="remove-powered-by" className="text-sm">
            Remove "Powered by Botyfi" branding from widget
          </label>
        </div>
      </div>

      <div className="border border-[#E5E7EB] p-6 bg-[#F9FAFB]">
        <h2 className="font-heading text-lg font-bold mb-4">Live Preview</h2>

        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl shadow-lg"
            style={{ background: primaryColor }}
          >
            💬
          </div>

          <div>
            <div className="font-semibold text-sm">{wlConfig.brand_name}</div>
            <div className="text-xs text-[#6B7280]">
              {wlConfig.remove_powered_by
                ? "No powered-by branding"
                : 'Shows "Powered by Botyfi"'}
            </div>
          </div>
        </div>

        <div className="mt-4 border border-[#E5E7EB] rounded-lg overflow-hidden bg-white w-80">
          <div
            className="px-4 py-3 text-white text-sm font-semibold"
            style={{ background: primaryColor }}
          >
            {wlConfig.brand_name}
          </div>
          <div className="p-4 bg-[#FAFAFA] h-24 flex items-end">
            <div className="bg-white border border-[#E5E7EB] rounded-2xl px-3 py-2 text-xs">
              Hi! How can I help you today?
            </div>
          </div>
          {!wlConfig.remove_powered_by && (
            <div className="text-center text-[10px] text-[#9CA3AF] py-2 border-t border-[#F3F4F6]">
              Powered by <span style={{ color: primaryColor, fontWeight: 600 }}>Botyfi</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={saveConfig}
          disabled={saving || !hasChanges}
          className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white text-sm h-10 px-8 transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
        {hasChanges && (
          <Button
            onClick={handleReset}
            variant="outline"
            className="rounded-none border-[#D1D5DB] text-[#6B7280] text-sm h-10 px-6"
          >
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
